import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
    AppId,
    FCX,
    HeatScoreLabel,
    MacStageLabel,
    usePlatformContext,
} from "@datassential/platform-ui-lib";
import cn from "classnames";
import { routeNames } from "@core/routeNames";
import Table from "@components/new/Table/Table";
import { iTableConfigItem } from "@components/new/Table/Table.helpers";
import HomeSectionSubTitle from "@pages/Home/components/HomeSectionSubTitle";
import { WhatsHotItem } from "@apiHooks/useHomeWhatsHotData";
import { HEAT_SCORES } from "@core/heatScores";
import { MAC_STAGES } from "@core/macStages";
import { EMPTY_CELL_VALUE, HOME_TABLE_COL_HIGHLIGHT_STYLE } from "@pages/Home/constants";
import MetricHeader, { MetricColumn } from "./MetricHeader";
import { FormattedNumber, PercentageCell, PercentageWithSign, MacStageTooltipHeader, HeatScoreTooltipHeader } from "./TableCellComponents";
import {
    TABLE_CONFIG,
    METRIC_COLORS,
    METRIC_GRADIENTS,
    COLUMN_SPANS,
} from "./constants";
import "./HomeTrendingTable.styles.scss";

interface HomeTrendingTableProps {
  data: WhatsHotItem[];
  isGrowingActive?: boolean;
}

const findIdByName = (arr: { name: string; id: number }[], name?: string | null) => (
    arr.find(item => item.name.toLowerCase() === name?.toLowerCase())?.id
)

const HomeTrendingTable: FCX<HomeTrendingTableProps> = ({
    data,
    isGrowingActive = false,
}) => {
    const animationDirection = isGrowingActive ? 'left' : 'right';
    const {
        userInfo: { products }
    } = usePlatformContext();
    const consumerPreferencesProduct = products[AppId.ConsumerPreferences];
    const menuTrendsProduct = products[AppId.MenuTrends];

    const config: iTableConfigItem[] = useMemo(() => [
        {
            title: "",
            styles: { 
                cell: { padding: TABLE_CONFIG.CELL_PADDING }, 
                head: { height: TABLE_CONFIG.HEADER_HEIGHT } 
            },
            Value: ({ index }) => (
                <div className="HomeTrendingTable__name">
                    <span className="HomeTrendingTable__number">{index + 1}.</span>
                    <Link
                        to={{
                            pathname: routeNames.food,
                            search: `?q=${encodeURIComponent(data[index].name || '')}`
                        }}
                    >
                        {data[index].name}
                    </Link>
                </div>
            )
        },
        {
            title: "# P1M",
            textCentered: true,
            styles: {
                head: { height: TABLE_CONFIG.HEADER_HEIGHT },
                cell: {
                    ...(isGrowingActive ? {} : HOME_TABLE_COL_HIGHLIGHT_STYLE),
                    borderRight: TABLE_CONFIG.BORDER_RIGHT
                },
            },
            Value: ({ index }) => <FormattedNumber value={data[index]?.mentions} />,
        },
        { 
            title: "# P1M",
            textCentered: true,
            styles: { head: { height: TABLE_CONFIG.HEADER_HEIGHT } },
            Value: ({ index }) => <FormattedNumber value={data[index]?.engagements} />,
        },
        {
            title: `% P1M\nGrowth`,
            textCentered: true,
            styles: {
                head: { height: TABLE_CONFIG.HEADER_HEIGHT },
                cell: {
                    ...(isGrowingActive ? HOME_TABLE_COL_HIGHLIGHT_STYLE : {}),
                    borderRight: TABLE_CONFIG.BORDER_RIGHT,
                },
            },
            Value: ({ index }) => <PercentageWithSign value={data[index]?.twelveMonthEngagementGrowth} />,
        },
        {
            textCentered: true,
            styles: { head: { height: TABLE_CONFIG.HEADER_HEIGHT }, cell: { borderRight: TABLE_CONFIG.BORDER_RIGHT } },
            renderTitle: <MacStageTooltipHeader />,
            Value: ({ index }) => {
                const id = findIdByName(MAC_STAGES, data[index]?.macStage);
                if (!id) return <>{EMPTY_CELL_VALUE}</>;
                return <MacStageLabel macStages={MAC_STAGES} macStageId={id} modifiers={['medium']} />;
            }
        },
        {
            title: "%",
            textCentered: true,
            styles: { head: { height: TABLE_CONFIG.HEADER_HEIGHT } },
            Value: ({ index }) => <PercentageCell value={data[index]?.penetration} /> 
        },
        {
            title: "% 4Y Growth",
            textCentered: true,
            styles: { head: { height: TABLE_CONFIG.HEADER_HEIGHT }, cell: { borderRight: TABLE_CONFIG.BORDER_RIGHT } },
            Value: ({ index }) => <PercentageWithSign value={data[index]?.fourYearPenetrationGrowth} />
        },
        {
            title: "Know It",
            textCentered: true,
            styles: { head: { height: TABLE_CONFIG.HEADER_HEIGHT } },
            Value: ({ index }) => <PercentageCell value={data[index]?.knowIt} />
        },
        {
            textCentered: true,
            styles: { head: { height: TABLE_CONFIG.HEADER_HEIGHT }, cell: { borderRight: TABLE_CONFIG.BORDER_RIGHT } },
            renderTitle: <HeatScoreTooltipHeader />,
            Value: ({ index }) => {
                const id = findIdByName(HEAT_SCORES, data[index]?.heatScore);
                if (!id) return <>{EMPTY_CELL_VALUE}</>;
                return <HeatScoreLabel heatScores={HEAT_SCORES} heatScoreId={id} modifiers={['medium']} />;
            }
        },
        {
            title: "Love/Like It",
            textCentered: true,
            styles: { head: { height: TABLE_CONFIG.HEADER_HEIGHT }, cell: { borderRight: TABLE_CONFIG.BORDER_RIGHT } },
            Value: ({ index }) => <PercentageCell value={data[index]?.loveLikeIt} />
        }
    ], [data]);

    const metrics: MetricColumn[] = useMemo(() => [
        {
            title: "",
            color: METRIC_COLORS.SOCIAL,
            columnGroups: [
                { title: <HomeSectionSubTitle>Top In Social</HomeSectionSubTitle>, colSpan: COLUMN_SPANS.SINGLE }
            ]
        },
        {
            title: "Social",
            color: METRIC_COLORS.SOCIAL,
            background: METRIC_GRADIENTS.SOCIAL,
            columnGroups: [
                { title: "Mentions", colSpan: COLUMN_SPANS.SINGLE },
                { title: "Engagements", colSpan: COLUMN_SPANS.DOUBLE }
            ]
        },
        {
            title: "Menu",
            color: METRIC_COLORS.MENU,
            background: METRIC_GRADIENTS.MENU,
            link: !menuTrendsProduct?.isDisabled ? menuTrendsProduct?.url : '',
            columnGroups: [
                { title: "", colSpan: COLUMN_SPANS.SINGLE },
                { title: "Penetration", colSpan: COLUMN_SPANS.DOUBLE }
            ]
        },
        {
            title: "Consumer",
            color: METRIC_COLORS.CONSUMER,
            background: METRIC_GRADIENTS.CONSUMER,
            link: !consumerPreferencesProduct?.isDisabled ? consumerPreferencesProduct?.url : '',
            columnGroups: [
                { title: "Awareness", colSpan: COLUMN_SPANS.DOUBLE },
                { title: "Affinity", colSpan: COLUMN_SPANS.SINGLE }
            ]
        }
    ], [products]);

    return (
        <div className={cn('HomeTrendingTable', `appears-${animationDirection}`)}>
            <MetricHeader columns={metrics} />
            <div className="HomeTrendingTable__body">
                <Table
                    config={config}
                    isCopyButtonHidden
                    rowsCount={data.length}
                    hasInnerScroll
                    rowHeight={50}
                />
            </div>
        </div>
    );
};

export default HomeTrendingTable;
